<template>
  <div
    ref="tabBarPanel"
    :class="[
      'itemTableContainer',
      $style.container,
      {
        [$style.containerInAsidePanel]: inDetailsAsidePanel,
      }]"
    :data-test-id="dataTestId"
  >
    <ItemTableEmptyMessage
      v-show="showEmptyMessage"
      ref="itemTableEmptyMessage"
      class="item-list"
      :emptyMessage="emptyCollectionMessage"
      :inModal="inModal"
      :linkText="linkText"
      :parentOS="parentOS"
      :route="route"
      :selectionItemText="selectionItemText"
    />
    <div
      v-show="!showEmptyMessage"
      ref="itemTableContent"
      :class="[$style.container, $style.contentContainer]"
    >
      <ItemTableActionBar
        v-if="actionBarVisible"
        v-bind="actionBarProps"
        :boundItemsUrl="boundItemsUrl"
        :canExport="canExport"
        :canModifyColumns="canModifyColumns"
        :columnOptions="columnOptions"
        :columns="columnsToDisplay"
        :disableBoundCount="disableBoundCount"
        :disableBulkAction="disableBulkAction"
        :disableSearchBar="disableSearchBar"
        :handleBulkAction="handleBulkAction"
        :handleBulkDelete="handleBulkDelete"
        :handleMainAction="handleMainAction"
        :handleSettingsButtonClick="handleSettingsButtonClick"
        :hideSelectionTotalCount="hideSelectionTotalCount"
        :inDetailsAsidePanel="inDetailsAsidePanel"
        :isAsideOpen="isAsideOpen"
        :isBulkMainActionDisabled="isBulkMainActionDisabled"
        :isMainActionDisabled="isMainActionDisabled"
        :isRefreshButtonEnabled="isRefreshButtonEnabled"
        :itemType="gettingStartedItemType ? gettingStartedItemType : itemType"
        :mainActionDisabledMessage="mainActionDisabledMessage"
        :mainActionMenuItems="mainActionMenuItems"
        :mainActionMenuLabel="mainActionMenuLabel"
        :placeholderText="placeholderText"
        :preventSearchReset="preventSearchReset"
        :reportType="reportType"
        :selectionItemText="selectionItemText"
        :setLoadingOverlay="setLoadingOverlay"
        :setSelectedModelId="setSelectedModelId"
        :showMainAction="showMainAction"
        :showManagePolicyGroupTemplates="showManagePolicyGroupTemplates"
        :showRefreshButton="showRefreshButton"
        :showSearchForm="showSearchForm"
        :showSelectedItemsCount="showSelectedItemsCount"
        :showSettingsButton="showSettingsButton"
        :storeModule="storeModule"
        :useDropDownFilterComponent="useDropDownFilterComponent"
        :useModernSearchStyles="useModernSearchStyles"
        :useSearchContextFilterComponent="useSearchContextFilterComponent"
        @columnsChange="handleColumnsChange"
        @revertColumns="handleRevertColumns"
      >
        <template #customBar>
          <slot name="customBar" />
        </template>
      </ItemTableActionBar>
      <ItemTableSearchBar
        v-if="showSearchForm && !showActionBar"
        :disableSearchBar="disableSearchBar"
        :placeholderText="placeholderText"
        :preventSearchReset="preventSearchReset"
        :selectionItemText="selectionItemText"
        :setLoadingOverlay="setLoadingOverlay"
        :storeModule="storeModule"
        :useDropDownFilterComponent="useDropDownFilterComponent"
        :useModernSearchStyles="useModernSearchStyles"
        :useSearchContextFilterComponent="useSearchContextFilterComponent"
      />
      <GettingStartedPanel
        :itemType="gettingStartedItemType ? gettingStartedItemType : itemType"
        :showTooltip="setGettingStartedTooltipVisibility"
        :visible="getGettingStartedVisible"
        @item-type-custom-content-active="setItemTypeCustomContentActive"
      />
      <slot name="emptyState" />
      <!--
        If the emptyState slot is not provided, then display the item table as normal.
        This allows an item table to show something in place of the table if there
        is an empty state or some other circumstance. Ideally we wouldn't have to
        do this but the item table encompasses too many other components right now.
      -->
      <div
        v-if="!$slots.emptyState && !getGettingStartedVisible && itemsLoaded"
        ref="container"
        :class="getTableContainerClass()"
        @scroll="handleScroll"
      >
        <table
          ref="table"
          :class="[$style.table, {
            [$style.tableWithoutSelectColumn]: !hasSelectColumn,
          }]"
        >
          <thead>
            <tr data-test-id="ItemTable__Columns">
              <th
                v-if="hasSelectColumn"
                :class="getTableCellClassName(true, { isSelectColumn: true })"
              >
                <label :class="$style.rowCheckboxContainer">
                  <input
                    :checked="!!selectedItemsCount"
                    :class="$style.rowCheckbox"
                    :disabled="disableSelectAllItems"
                    :indeterminate.prop="areOnlySomeItemsSelected"
                    type="checkbox"
                    @change="handleSelectAllCheckboxClick($event)"
                  >
                </label>
              </th>
              <th
                v-if="details && detailsIconLeft"
                :class="[getTableCellClassName(true), $style.detailsColumn]"
              />
              <th
                v-for="(column, index) in visibleColumns"
                :key="index"
                :class="getTableCellClassName(true, column)"
                data-test-id="ItemTable__Column"
              >
                <a
                  v-if="column.sortBy"
                  :class="[ $style.sortBy, sortClassName(column.sortBy) ]"
                  href="javascript:void(0);"
                  @click.prevent.stop="sortBy(column.sortBy)"
                >
                  {{ column.headingText }}
                </a>
                <a
                  v-if="column.customSort"
                  :class="[ $style.sortBy, customSortClassName() ]"
                  href="javascript:void(0);"
                  @click.prevent.stop="handleCustomSort(column.handleSort)"
                >
                  {{ column.headingText }}
                </a>
                <span v-if="!column.sortBy && !column.customSort">
                  {{ column.headingText }}
                </span>
                <IconTooltip
                  v-if="column.showHeadingTooltip"
                >
                  {{ column.headingTooltipText }}
                </IconTooltip>
              </th>
              <th
                v-if="details && !detailsIconLeft"
                :class="[getTableCellClassName(true), $style.detailsColumn]"
              />
            </tr>
          </thead>
          <tfoot v-if="showFooter">
            <tr>
              <td :colspan="colspan">
                <div :class="getFooterTextClass()">
                  {{ footer }}
                </div>
              </td>
            </tr>
          </tfoot>
          <tbody>
            <template v-for="(item, itemIndex) in items">
              <tr
                :key="`bodyRow-${getId(item)}`"
                :class="tableBodyRowClass(item)"
                data-test-id="ItemTable__itemRow"
                data-walkthrough="item-table-row"
                @click="handleRowClickMethod(getId(item))"
              >
                <td
                  v-if="hasSelectColumn"
                  :class="getTableCellClassName(false, { isSelectColumn: true })"
                >
                  <label
                    :class="$style.rowCheckboxContainer"
                    @click.stop
                  >
                    <input
                      :checked="selectedItems.includes(getId(item))"
                      :class="getSelectCheckboxClassName(getId(item))"
                      :disabled="!isItemSelectable(item) || isItemDisabled(item)"
                      type="checkbox"
                      :value="getId(item)"
                      @change="handleSelectItemClick(getId(item), $event)"
                    >
                  </label>
                </td>
                <td
                  v-if="details && detailsIconLeft"
                  :class="[getTableCellClassName(false), $style.detailsColumn]"
                >
                  <button
                    :class="detailsButtonClassName(getId(item))"
                    @click.stop="handleDetailsButtonClickMethod(getId(item))"
                  >
                    <i :class="getDetailsIconClass" />
                  </button>
                </td>
                <td
                  v-for="(column, index) in visibleColumns"
                  :key="index"
                  :class="getTableCellClassName(false, column)"
                >
                  <span
                    v-if="column.dataField"
                    :class="getDataFieldTextClass()"
                    :title="getColumnDataField(column, item)"
                  >
                    {{ getColumnDataField(column, item) }}
                  </span>
                  <component
                    :is="column.bodyComponent"
                    v-if="column.bodyComponent"
                    v-bind="getBodyComponentProps(column.bodyComponentProps)"
                    :disabled="isItemDisabled(item)"
                    :index="itemIndex"
                    :item="item"
                    :totalItemCount="items.length"
                  />
                </td>
                <td
                  v-if="details && !detailsIconLeft"
                  :class="[getTableCellClassName(false), $style.detailsColumn]"
                >
                  <button
                    :class="detailsButtonClassName(getId(item))"
                    @click.stop="handleDetailsButtonClickMethod(getId(item))"
                  >
                    <i :class="getDetailsIconClass" />
                  </button>
                </td>
                <slot
                  :item="item"
                  name="row-overlay"
                />
              </tr>
              <tr
                v-if="details"
                :key="`details-${getId(item)}`"
                data-test-id="ItemTable__detailsRow"
              >
                <td :colspan="colspan">
                  <div :class="expandableContentClassName(getId(item))">
                    <component
                      :is="detailsComponent"
                      :isExpanded="isExpanded(getId(item))"
                      :item="item"
                    />
                  </div>
                </td>
              </tr>
            </template>
            <tr
              v-if="showLoadingIndicator"
              :data-test-id="$testId('scrollBottomLoadingIndicator')"
            >
              <td
                :class="$style.loadingIndicator"
                :colspan="colspan"
              >
                <i :class="[ 'jc-progress', 'jc-spin', $style.loadingIndicatorIcon ]" />
                Loading more items...
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <LoadingOverlay
      :loadingOverlayText="loadingOverlayText"
      :showLoadingOverlay="showLoadingOverlay"
      :zIndex="Number(itemTableZIndex) + 1"
    />
  </div>
</template>

<script>
import {
  debounce, extend, get, values,
} from 'lodash';
import { mapActions, mapGetters, mapState } from 'vuex';
import axios from 'axios';

import {
  ItemTableConsts,
  ItemTypeConsts,
  ItemTypeLabels,
  StoreModuleMap,
} from 'client/util/Constants';
import { eventProperties, itemTypePages } from 'client/GrowthEngineering/utilities/GrowthConstants';
import { itemTableZIndex } from 'client/css/z-index.css';
import { notify } from 'client/util';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import GettingStartedPanel from 'client/components/GettingStartedPanel/GettingStartedPanel.vue';
import IconTooltip from 'client/components/Tooltip/IconTooltip.vue';
import ItemTableColumnsMap from 'components/ItemTable/ItemTableColumnsMap';
import ItemTablePropsMixin from 'client/components/ItemTable/mixins/ItemTablePropsMixin';
import LoadingOverlay from 'client/components/LoadingOverlay/LoadingOverlay.vue';
import Vue from 'vue';

import ActionCommandButton from './BodyComponents/ActionCommandButton.vue';
import AdBridgeLabel from './BodyComponents/AdBridgeLabel.vue';
import AdBridgeVersion from './BodyComponents/AdBridgeVersion.vue';
import AdminDetailButton from './BodyComponents/AdminDetailButton.vue';
import AdminRole from './BodyComponents/AdminRole.vue';
import BindStatus from './BodyComponents/BindStatus.vue';
import CommandOSIcon from './BodyComponents/CommandOSIcon.vue';
import CommandTemplateConfigureButton from './BodyComponents/CommandTemplateConfigureButton.vue';
import CommandTemplateDescription from './BodyComponents/CommandTemplateDescription.vue';
import CommandTemplateName from './BodyComponents/CommandTemplateName.vue';
import DefaultDeviceGroup from './BodyComponents/DefaultDeviceGroup.vue';
import DirectoryAccessConfiguration from './BodyComponents/DirectoryAccessConfiguration.vue';
import DirectoryTypeIcon from './BodyComponents/DirectoryTypeIcon.vue';
import DismissButton from './BodyComponents/DismissButton.vue';
import EditableInput from './BodyComponents/EditableInput.vue';
import EditableInputValidator from './BodyComponents/EditableInputValidator.vue';
import EditableInputWithIcon from './BodyComponents/EditableInputWithIcon.vue';
import FormattedDate from './BodyComponents/FormattedDate.vue';
import GroupTypeIcon from './BodyComponents/GroupTypeIcon.vue';
import ImportStatus from './BodyComponents/ImportStatus.vue';
import ItemLink from './BodyComponents/ItemLink.vue';
import ItemName from './BodyComponents/ItemName.vue';
import ItemTableActionBar from './ItemTableActionBar.vue';
import ItemTableEmptyMessage from './ItemTableEmptyMessage.vue';
import ItemTableSearchBar from './ItemTableSearchBar.vue';
import PasswordNeverExpiresText from './BodyComponents/PasswordNeverExpiresText.vue';
import PasswordStatusIcon from './BodyComponents/PasswordStatusIcon.vue';
import PolicyDescription from './BodyComponents/PolicyDescription.vue';
import PolicyTemplateConfigureButton from './BodyComponents/PolicyTemplateConfigureButton.vue';
import PrimaryAdapterIP from './BodyComponents/PrimaryAdapterIP.vue';
import RequirementsDetail from './BodyComponents/RequirementsDetail.vue';
import SambaAuthentication from './BodyComponents/SambaAuthentication.vue';
import StatusIcon from './BodyComponents/StatusIcon.vue';
import SystemOSIcon from './BodyComponents/SystemOSIcon.vue';
import SystemSudoStatus from './BodyComponents/SystemSudoStatus.vue';
import SystemUserGroupCount from './BodyComponents/SystemUserGroupCount.vue';
import SystemUserMFABadgeWithTooltip from './BodyComponents/SystemUserMFABadgeWithTooltip.vue';
import SystemUserMFAIcon from './BodyComponents/SystemUserMFAIcon.vue';
import SystemUserMFARequirementBadgeWithTooltip from './BodyComponents/SystemUserMFARequirementBadgeWithTooltip.vue';
import SystemUserSudoStatus from './BodyComponents/SystemUserSudoStatus.vue';
import UserMFAIcon from './BodyComponents/UserMFAIcon.vue';
import UserState from './BodyComponents/UserState.vue';
import ViewDetailsButton from './BodyComponents/ViewDetailsButton.vue';
import ViewRecoveryKeyButton from './BodyComponents/ViewRecoveryKeyButton.vue';
import WorkdayWorkerPreviewAttributes from './BodyComponents/WorkdayWorkerPreviewAttributes.vue';

const { detailsButtonType } = ItemTableConsts;
const organizationsCollection = StoreModuleMap[ItemTypeConsts.organizations];
const settingsModule = StoreModuleMap[ItemTypeConsts.settings];

const {
  boolean,
  description,
  location,
  section,
  text,
} = eventProperties;

export default {
  name: 'ItemTable',

  components: {
    ActionCommandButton,
    AdBridgeLabel,
    AdBridgeVersion,
    AdminDetailButton,
    AdminRole,
    BindStatus,
    CommandTemplateDescription,
    CommandOSIcon,
    CommandTemplateName,
    DefaultDeviceGroup,
    DirectoryAccessConfiguration,
    DirectoryTypeIcon,
    DismissButton,
    EditableInput,
    EditableInputValidator,
    EditableInputWithIcon,
    FormattedDate,
    GroupTypeIcon,
    GettingStartedPanel,
    IconTooltip,
    ImportStatus,
    ItemLink,
    ItemName,
    ItemTableActionBar,
    ItemTableEmptyMessage,
    ItemTableSearchBar,
    LoadingOverlay,
    PasswordNeverExpiresText,
    PasswordStatusIcon,
    PolicyDescription,
    PolicyTemplateConfigureButton,
    CommandTemplateConfigureButton,
    PrimaryAdapterIP,
    RequirementsDetail,
    SambaAuthentication,
    StatusIcon,
    SystemOSIcon,
    SystemSudoStatus,
    SystemUserGroupCount,
    SystemUserMFABadgeWithTooltip,
    SystemUserMFAIcon,
    SystemUserMFARequirementBadgeWithTooltip,
    SystemUserSudoStatus,
    UserMFAIcon,
    UserState,
    ViewDetailsButton,
    ViewRecoveryKeyButton,
    WorkdayWorkerPreviewAttributes,
  },

  mixins: [ItemTablePropsMixin],

  props: {
    clickRowExpandsDetails: Boolean,
    /**
     * Specify an array of filters that will be set for the collection in the
     * mounted handler to limit the items returned from the fetchCollection
     * action. These filters will be passed to the collection prior to fetching
     * the initial set of items.
     */
    collectionFilters: {
      type: Array,
      default: () => [],
    },
    columnOptions: {
      type: Array,
      default: () => [],
      validator: options => options.every(d => (
        typeof d.name === 'string' && typeof d.value === 'string'
      )),
    },
    columns: {
      type: Array,
      required: true,
    },
    dataTestId: {
      type: String,
      default: undefined,
    },
    defaultColumns: {
      type: Array,
      default: () => [],
    },
    disableBulkAction: Boolean,
    disableGettingStarted: Boolean,
    disableSelectAllItems: Boolean,
    disableSelectItems: Boolean,
    footer: {
      default: '',
      type: String,
    },
    handleBulkAction: {
      type: Function,
      default: () => {},
    },
    handleBulkDelete: {
      type: Function,
      default: () => {},
    },
    handleDetailsButtonClick: {
      type: Function,
      default: () => {},
    },
    handleMainAction: {
      type: Function,
      default: () => {},
    },
    handleRowClick: {
      type: Function,
      default: () => {},
    },
    handleSettingsButtonClick: {
      type: Function,
      default: () => {},
    },
    handleUnselectLockedItem: {
      type: Function,
      default: () => {},
    },
    handleUnselectMultipleLockedItems: {
      type: Function,
      default: () => Promise.resolve(),
    },
    hasSelectColumn: Boolean,
    hideSelectionTotalCount: Boolean,
    inModal: Boolean,
    isBulkMainActionDisabled: Boolean,
    isItemLocked: {
      type: Function,
      default: () => false,
    },
    isMainActionDisabled: Boolean,
    isRefreshButtonEnabled: {
      type: Boolean,
      default: true,
    },
    itemType: {
      type: String,
      required: true,
      validator: function validator(value) {
        return values(ItemTypeConsts).indexOf(value) !== -1;
      },
    },
    loadingOverlayText: {
      default: null,
      type: String,
    },
    mainActionDisabledMessage: {
      type: String,
      default: '',
    },
    mainActionMenuItems: {
      type: Array,
      default: () => [],
    },
    mainActionMenuLabel: {
      type: String,
      default: '',
    },
    onMountedAfterFetch: {
      type: Function,
      default: () => {},
    },
    /**
     * Specify a method that will be called in the mounted handler to set
     * collection specific parameters. This method will be called prior to
     * fetching the initial set of items.
     */
    onMountedBeforeFetch: {
      type: Function,
      default: () => {},
    },
    placeholderText: {
      type: String,
      default: 'Search',
    },
    preventSearchReset: {
      type: Boolean,
      default: false,
    },
    reportType: {
      type: String,
      default: '',
    },
    route: {
      type: String,
      default: '',
    },
    showManagePolicyGroupTemplates: {
      type: Boolean,
      default: false,
    },
    showSelectedItemsCount: {
      type: Boolean,
      default: true,
    },
    showSettingsButton: Boolean,
  },

  data() {
    return {
      columnsToDisplay: this.columns,
      customSortOrder: '',
      expandedIds: [],
      gettingStartedItemTypeCustomContentActive: false,
      isMounted: false,
      itemsBeingFetched: false,
      itemTableZIndex,
      keepFreshErrorCount: 0,
      keepFreshInterval: null,
      prevScrollTop: null,
      scrolling: false,
      showFooter: !!this.footer,
      showLoadingOverlay: true,
      visible: true,
      actionBarProps: {
        additionalBulkActions: this.additionalBulkActions,
        bulkActionsList: this.bulkActionsList,
        bulkActionText: this.bulkActionText,
        bulkDeleteText: this.bulkDeleteText,
        eventBus: new Vue(),
        showBound: this.showBound,
        showBulkAction: this.showBulkAction,
        showBulkDelete: this.showBulkDelete,
        showManagePolicyGroupTemplates: this.showManagePolicyGroupTemplates,
      },
    };
  },

  computed: {
    ...mapState(settingsModule, ['orgId']),
    ...mapGetters(organizationsCollection, ['getItemDetails']),

    /**
     * action bar should be hidden when certain custom content
     * from getting started panel is displayed,
     * some times content related to experiments.
     * By doing this, main action will be hidden too
     * and users are forced to use the actions inside custom content
     * to get access to the original main actions
     */
    actionBarVisible() {
      if (this.gettingStartedItemTypeCustomContentActive) {
        if (!this.itemsLoaded) {
          return false;
        }
        if (this.getGettingStartedVisible) {
          return false;
        }
        return this.showActionBar;
      }

      return this.showActionBar;
    },

    colspan() {
      let colspan = this.columnsToDisplay.length;
      if (this.hasSelectColumn) colspan += 1;
      if (this.details) colspan += 1;
      return colspan;
    },

    getDetailsIconClass() {
      const classes = [];
      if (this.detailsButtonType === detailsButtonType.default
        || this.detailsButtonType === detailsButtonType.caret) {
        classes.push('jc-details-edit', this.$style.detailsArrowIcon);
      } else if (this.detailsButtonType === detailsButtonType.rowExpand) {
        classes.push('jc-etc');
      }
      return classes;
    },

    getGettingStartedVisible() {
      return !this.inDetailsAsidePanel
        && !this.showLoadingOverlay
        && !this.disableGettingStarted
        && this.isNumberOfItemsZero;
    },

    isNumberOfItemsZero() {
      // Special handling for system users table. Because we have
      // top-level filters on the table, we need to use the total
      // number of system users in the org, not the all or active
      // items counts.
      if (this.itemType === ItemTypeConsts.systemUser) {
        // a default of -1 here, allows for a better UX if for some reason the underlying
        // api call fails. A default of 0 would perpetually show the getting started panel
        // and that seems undesirable.
        const count = get(this.orgDetails, 'systemUsersCount', -1);
        return count === 0;
      }

      return (this.totalCountOfAllItems === 0 || this.totalCountOfActiveItems === 0);
    },

    itemsLoaded() {
      return this.totalCountOfAllItems !== null && this.totalCountOfActiveItems !== null;
    },

    orgDetails() {
      return this.getItemDetails(this.orgId);
    },

    showEmptyMessage: {
      get() {
        if (this.showLoadingOverlay) {
          return false;
        }

        if (this.inDetailsAsidePanel || this.inModal || this.inSecondaryMainPanel) {
          return this.totalCountOfAllItems === 0;
        }

        if (!this.visible) {
          return true;
        }

        return false;
      },
    },

    showLoadingIndicator: {
      get() {
        if (this.showLoadingOverlay) return false;

        const hasToken = !!(this.nextPageToken || this.skipToken);
        const itemCount = this.items?.length || 0;

        return (itemCount < this.totalCount) || hasToken;
      },
    },

    storeModule() {
      return StoreModuleMap[this.itemType];
    },

    visibleColumns() {
      return this.columnsToDisplay.filter((_, index) => this.shouldColumnBeVisible(index));
    },
  },

  watch: {
    items() {
      this.toggleHeader();
    },

    searchTerm(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.collapseExpandableContent();
      }
    },

    visible() {
      this.toggleHeader();
    },
  },

  /**
   * Extend the computed properties and methods here, before the component is
   * created so that the namespace argument to mapActions() and mapGetters()
   * can be set dynamically from the props passed to the ItemTable component.
   * @see https://github.com/vuejs/vuex/issues/863#issuecomment-315619012
   */
  beforeCreate() {
    const storeModule = StoreModuleMap[this.$options.propsData.itemType];

    if (!storeModule) {
      throw new Error('Could not find a storeModule to populate the item table.');
    }

    extend(
      this.$options.computed,
      mapGetters(storeModule, {
        areAllItemsSelected: 'areAllItemsSelected',
        areOnlySomeItemsSelected: 'areOnlySomeItemsSelected',
        boundItemsTotalCount: 'getBoundItemsTotalCount',
        isItemIndirectlyBound: 'isItemIndirectlyBound',
        isItemSelectable: 'isItemSelectable',
        items: 'getCollection',
        nextPageToken: 'getNextPageToken',
        skipToken: 'getSkipToken',
        pathInfoForParent: 'getPathInfoForParent',
        searchTerm: 'getSearchTerm',
        selectedItems: 'getSelectedItems',
        selectedItemsCount: 'getSelectedItemsCount',
        sortField: 'getSortField',
        totalCount: 'getTotalCount',
        totalCountOfAllItems: 'getTotalCountOfAllItems',
        totalCountOfActiveItems: 'getTotalCountOfActiveItems',
      }),

      mapState(storeModule, {
        showBoundItems: state => state.features.showBoundItems,
        usePaginationMethodForBoundDevices: state => state
          .features.usePaginationMethodForBoundDevices,
      }),
    );

    extend(
      this.$options.methods,
      mapActions(storeModule, [
        'deleteItems',
        'fetchCollection',
        'refreshCollection',
        'resetCollection',
        'selectAllItems',
        'selectItem',
        'setBoundItemsUrl',
        'setFilter',
        'setShowBoundItems',
        'setSortField',
        'unselectAllItems',
        'unselectItem',
        'unselectItems',
      ]),
    );

    // These getters are mapped to the methods, rather than computed properties
    // because they're called externally from the VueComponents wrapper.
    extend(
      this.$options.methods,
      mapGetters(storeModule, {
        getItem: 'getItem',
        getItemsToBind: 'getItemsToBind',
        getItemsToUnbind: 'getItemsToUnbind',
      }),
    );
  },

  mounted() {
    this.handleMounted();
  },

  updated() {
    this.toggleHeader();
  },

  destroyed() {
    this.resetCollection();
    this.cancelKeepFresh();
  },

  methods: {
    cancelKeepFresh() {
      window.clearInterval(this.keepFreshInterval);
      this.keepFreshInterval = null;
    },

    collapseExpandableContent() {
      this.expandedIds.length = 0;
    },

    customSortClassName() {
      let className = '';
      if (this.customSortOrder && !this.sortField) {
        className = this.customSortOrder === 'asc' ? this.$style.sortActiveAsc
          : this.$style.sortActiveDesc;
      }
      return className;
    },

    // Note: be aware that 250ms is just a reference
    // if needed, this value could increase or decrease
    debounceResetIsScrolling: debounce(function handle() {
      this.scrolling = false;
    }, 250),

    detailsButtonClassName(id) {
      const caret = this.detailsButtonType === detailsButtonType.caret;
      const classes = [this.$style.detailsButton];

      if (this.isExpanded(id)) {
        classes.push(caret ? this.$style.caretExpanded : this.$style.detailsButtonExpanded);
      } else if (caret) {
        classes.push(this.$style.caretCollapsed);
      }

      return classes;
    },

    expandableContentClassName(id) {
      if (this.isExpanded(id)) {
        return this.$style.expandableContentVisible;
      }
      return this.$style.expandableContent;
    },

    fetchIfReachedBottom() {
      if (!this.hasReachedBotttom()) {
        return;
      }
      this.itemsBeingFetched = true;
      this
        .fetchCollection({ next: true })
        .catch(() => {
          notify(
            'error',
            `Unable to fetch ${this.$props.selectionItemText.plural}!`,
          );
        })
        .finally(() => {
          this.itemsBeingFetched = false;
        });
    },

    getBodyComponentProps(bodyComponentProps) {
      return {
        itemType: this.itemType,
        inDetailsAsidePanel: this.inDetailsAsidePanel,
        ...bodyComponentProps,
      };
    },

    getColumnDataField(column, item) {
      const value = get(item, column.dataField, undefined);

      if (column.defaultValue && value === undefined) return column.defaultValue;

      return value;
    },

    getDataFieldTextClass() {
      const textClasses = [this.$style.dataFieldText];

      return textClasses;
    },

    getFooterTextClass() {
      const textClasses = [this.$style.footerText];

      return textClasses;
    },

    getId(item) {
      return get(item, this.idField);
    },

    getSelectCheckboxClassName(id) {
      const classes = [this.$style.rowCheckbox];
      if (this.isItemLocked(id)) {
        classes.push(this.$style.checkboxLocked);
      }
      return classes;
    },

    getTableCellClassName(isHeading, column) {
      const classNames = [isHeading ? this.$style.tableHeadCell : this.$style.tableBodyCell];

      if (this.useLargeTableCells) {
        classNames.push(isHeading
          ? this.$style.largeTableCellHeader
          : this.$style.largeTableCellBody);
      }

      if (column && column.classNames) {
        if (isHeading && column.classNames.heading && column.classNames.heading.length) {
          column.classNames.heading.forEach((className) => {
            if (this.$style[className]) {
              classNames.push(this.$style[className]);
            } else {
              classNames.push(className);
            }
          });
        } else if (!isHeading && column.classNames.body && column.classNames.body.length) {
          column.classNames.body.forEach((className) => {
            if (this.$style[className]) {
              classNames.push(this.$style[className]);
            } else {
              classNames.push(className);
            }
          });
        }
      }

      if (column && column.isSelectColumn) {
        classNames.push(this.$style.selectColumn);
      }

      return classNames;
    },

    getTableContainerClass() {
      const containerClasses = [this.$style.tableContainer];

      if (!this.inDetailsAsidePanel || this.inModal) {
        containerClasses.push(this.$style.tableContainerInMainPanel);
      }

      return containerClasses;
    },

    handleColumnsChange(columnNames) {
      // Given a set of column names, return the full definitions of these columns
      const columnDefinitions = ItemTableColumnsMap[this.itemType];
      if (columnDefinitions) {
        this.columnsToDisplay = columnNames.map(columnName => (
          Object.values(columnDefinitions)
            .find(d => [d.name, d.headerName].includes(columnName))
        ));
      }
      this.$emit('columnsChange', columnNames);
    },

    handleCustomSort(handler) {
      if (this.customSortOrder) {
        this.customSortOrder = this.customSortOrder === 'asc' ? 'desc' : 'asc';
      } else {
        this.customSortOrder = 'asc';
      }
      handler(this.customSortOrder);
    },

    /**
     * @description If this is a details row and it's already expanded, collapse it;
     * otherwise expand it to show the details.
     * If this is not a details row - perform the default action (opening the aside).
     */
    handleDetailsButtonClickMethod(id) {
      if (this.detailsButtonType === detailsButtonType.rowExpand
        || this.detailsButtonType === detailsButtonType.caret) {
        const index = this.expandedIds.indexOf(id);

        if (index !== -1) {
          this.expandedIds.splice(index, 1);
        } else {
          this.expandedIds.push(id);
        }
      }

      this.handleDetailsButtonClick(id);
    },

    /**
     * @description Keep the current items fresh by polling for changes. This
     * method will only refresh the item list if the user is not scrolling. In
     * addition, the refreshCollection() action in the store will not refresh
     * the collection if searching, filtering, or when item's have been
     * selected.
     */
    handleKeepFresh() {
      const handleRefreshError = () => {
        this.keepFreshErrorCount += 1;

        if (this.keepFreshErrorCount >= 3) {
          this.cancelKeepFresh();
        }
      };

      if (!this.scrolling && !this.itemsBeingFetched) {
        this
          .refresh()
          .catch(handleRefreshError.bind(this));
      }
    },

    handleMounted() {
      this.resetCollection();
      this.setBoundItemsUrl({ url: this.$props.boundItemsUrl });

      if (this.showBoundInitially && !this.isMounted) {
        this.setShowBoundItems(true);
      }

      this.setCollectionFilters(this.collectionFilters);

      if (this.defaultSortField) {
        this.setSortField({ sortField: this.defaultSortField });
      }
      this.onMountedBeforeFetch();

      this.fetchCollection()
        .then(() => {
          // When initially showing bound items, and there are no bound items, then show all items!
          if (this.showBoundInitially && this.isMounted === false && this.totalCount === 0) {
            this.isMounted = true;
            this.setShowBoundItems(false);

            window.setTimeout(() => {
              this.handleMounted();
            }, 100);

            return;
          }

          this.isMounted = true;
          this.showLoadingOverlay = false;
          this.onMountedAfterFetch();
        })
        .catch(() => {
          this.showLoadingOverlay = false;
          notify(
            'error',
            `Unable to fetch ${this.$props.selectionItemText.plural}!`,
          );
        });

      this.startKeepFresh();
    },

    handleRevertColumns() {
      this.$emit('revertColumns');
      this.columnsToDisplay = [...this.defaultColumns];
    },

    /**
     * @description If this is an expandable row, pass the click to the details
     * button.  Otherwise, perform a normal row click.
     */
    handleRowClickMethod(id) {
      if (this.clickRowExpandsDetails) {
        this.handleDetailsButtonClickMethod(id);
      } else {
        this.handleRowClick(id);
      }
    },

    handleScroll(e) {
      // Just return if the container reference is undefined. A scroll event is
      // triggered when scrolled down into the ItemTable and it is destroyed when
      // the aside panel is closing.
      if (!this.$refs.container) {
        return;
      }

      this.scrolling = true;
      this.debounceResetIsScrolling();

      const { scrollTop } = e.target;
      if (scrollTop < this.prevScrollTop) {
        this.prevScrollTop = scrollTop;
        return;
      }
      this.prevScrollTop = scrollTop;

      if (this.itemsBeingFetched) {
        return;
      }

      this.fetchIfReachedBottom();
    },

    handleSelectAllCheckboxClick($event) {
      const { checked } = $event.target;

      if (
        (this.usePaginationMethodForBoundDevices && checked)
        || (!this.areAllItemsSelected && !this.areOnlySomeItemsSelected)
      ) {
        this.showLoadingOverlay = true;
        this.selectAllItems()
          .then(() => {
            this.showLoadingOverlay = false;
            EventHelper.triggerCheckboxClicked({
              [description]: `Select All checkbox from ${itemTypePages[this.itemType]} list page`,
              [location]: itemTypePages[this.itemType],
              [section]: `${this.$props.selectionItemText.plural} Item Table`,
              [text]: checked ? 'select all' : 'unselect all',
              [boolean]: checked ? 1 : 0,
            });
          })
          .catch(() => {
            this.showLoadingOverlay = false;
            notify(
              'error',
              `Unable to select all ${this.$props.selectionItemText.plural}!`,
            );
          });
      } else {
        const unselectAllItems = () => {
          if (!this.usePaginationMethodForBoundDevices && this.areOnlySomeItemsSelected) {
            this.unselectItems({ ids: this.selectedItems });
            this.showLoadingOverlay = false;
          } else {
            this.unselectAllItems()
              .then(() => {
                this.showLoadingOverlay = false;
                EventHelper.triggerCheckboxClicked({
                  [description]: `Select All checkbox from ${itemTypePages[this.itemType]} list page`,
                  [location]: itemTypePages[this.itemType],
                  [section]: `${this.$props.selectionItemText.plural} Item Table`,
                  [text]: checked ? 'select all' : 'unselect all',
                  [boolean]: checked ? 1 : 0,
                });
              })
              .catch(() => {
                this.showLoadingOverlay = false;
                notify(
                  'error',
                  `Unable to unselect all ${this.$props.selectionItemText.plural}!`,
                );
              });
          }
        };

        this.showLoadingOverlay = true;

        // When unselecting all items the the total number of selected items
        // may be larger than the set of selected items shown. This case can
        // occur when a search term is entered and the set of visible items is
        // smaller than the set of all selected items. In this case, it's
        // preferred to inspect the items that are currently available to see
        // if any are locked.
        const lockedIds = this.items.reduce((acc, item) => {
          if (this.isItemLocked(this.getId(item))) {
            acc.push(this.getId(item));
          }
          return acc;
        }, []);

        if (lockedIds.length) {
          // eslint-disable-next-line no-param-reassign
          $event.target.checked = true;

          this
            .handleUnselectMultipleLockedItems(lockedIds)
            .then((canBeRemoved) => {
              if (canBeRemoved) {
                unselectAllItems();
              } else {
                this.showLoadingOverlay = false;
              }
            })
            .catch(() => {
              notify('error', 'Unable to remove users from user groups!');
            });
        } else {
          unselectAllItems();
        }
      }
    },

    handleSelectItemClick(id, $event) {
      const { checked } = $event.target;

      // Allow the parent ItemTable wrapper to handle the business logic of
      // unselecting a locked item. Locked items may be bound indirectly to the
      // parent item, e.g. a user is in a user group that is bound to a system
      // group that contains the system, or in the case of JumpCloud LDAP the
      // parent item, i.e. a user, may be an LDAP Bind DN or the Samba Service
      // Account. Because these edge cases are complex, and not generic, they
      // are handled by the parent component.
      if (this.isItemLocked(id) && !checked) {
        // eslint-disable-next-line no-param-reassign
        $event.target.checked = true;
        this.handleUnselectLockedItem(id);
        return;
      }

      if (checked) {
        this.selectItem({ id });
      } else {
        this.unselectItem({ id });
      }

      EventHelper.triggerCheckboxClicked({
        [boolean]: checked ? 1 : 0,
        [description]: `Toggles the status of an individual ${ItemTypeLabels[this.itemType]}`,
        [location]: itemTypePages[this.itemType],
        [section]: `${this.$props.selectionItemText.plural} Item Table`,
      });
    },

    /**
     * Return true if the user has scrolled to within 250px of the bottom of
     * the table to fetch more users.
     */
    hasReachedBotttom() {
      const containerHeight = this.$refs.container.getBoundingClientRect().height;
      const containerScrollTop = this.$refs.container.scrollTop;
      const tableHeight = this.$refs.table.getBoundingClientRect().height;

      return (tableHeight - (containerScrollTop + containerHeight)) <= 250;
    },

    hide() {
      this.visible = false;
    },

    isExpanded(id) {
      return this.expandedIds.indexOf(id) !== -1;
    },

    isItemDisabled(item) {
      return this.disableSelectItems && !this.selectedItems.includes(this.getId(item));
    },

    /**
     * Call the refreshCollection() method on the CollectionModule to get a
     * fresh copy of the items that are currently scrolled into view in the
     * ItemTable. This is necessary so when the admin user is viewing an
     * ItemTable if items are added or removed the UI can be updated to display
     * them.
     * @see refreshCollection in /client/store/modules/CollectionModule.js for
     * more details on the logic of refreshing a collection.
     * @todo It may make sense for the collection to prevent fetching or
     * refreshing items if already doing so. i.e. Create a mutex to prevent
     * two different actions from doing this at the same time. The second
     * request would simply get droppped.
     */
    refresh() {
      // First, determine the approximate skip parameter to pass to
      // refreshCollection() based on the current scrollTop value of the
      // container and the average height of a table row.

      if (!this.$refs.container) {
        return Promise.resolve();
      }

      const { scrollTop } = this.$refs.container;

      if (!this.$refs.table) {
        return Promise.resolve();
      }

      const tr = this.$refs.table.querySelector(`.${this.$style.tableBodyRow}`);
      let skip = 0;

      if (tr) {
        const trHeight = tr.getBoundingClientRect().height;
        skip = parseInt(scrollTop / trHeight, 10);
        skip = Number.isNaN(skip) ? 0 : skip;
      }

      // Then call refreshCollection() specifying the skip parameter so that
      // the UI can be updated.
      return this.refreshCollection({ skip });
    },

    setCollectionFilters(filters = []) {
      filters.forEach(filter => this.setFilter({ filter }));
    },

    /**
     * This method is called when the <GettingStartedPanel> emits the
     * experiment-active event when a getting started experiment is being
     * shown. This data property is used to hide the action bar
     * when the experiment is shown to force the user to engage with
     * the experiment and have a cleaner view
     */
    setItemTypeCustomContentActive() {
      if (!this.inDetailsAsidePanel) {
        this.gettingStartedItemTypeCustomContentActive = true;
      }
    },

    setLoadingOverlay(loadingState) {
      this.showLoadingOverlay = loadingState;
    },

    shouldColumnBeVisible(index) {
      // If this is a main panel and the aside is open, only show the first three columns
      if (!this.inDetailsAsidePanel && this.isAsideOpen) {
        if (index > 2) {
          return false;
        }
      }

      return true;
    },

    show() {
      this.visible = true;
    },

    /**
     * @todo When sorting there is a noticable shift when the collection is
     * reset prior to being set again. This happens because the columns don't
     * have defined widths. It feels like the only solution is to specify
     * percentage widths for all columns. This issue will have be addressed,
     * but is being held off on for now to continue working on other required
     * funtionality.
     */
    sortBy(sortField) {
      this.showLoadingOverlay = true;
      this.customSortOrder = '';

      this.setSortField({ sortField });
      this.fetchCollection()
        .then(() => {
          this.showLoadingOverlay = false;
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            return;
          }
          this.showLoadingOverlay = false;
          notify(
            'error',
            `Unable to sort ${this.$props.selectionItemText.plural}!`,
          );
        });
    },

    /**
     * Determine whether to display an ascending or descending sort arrow
     */
    sortClassName(sortField = '') {
      /**
       * Check if it's being currently sorted by this sort field by removing
       * dash signs (e.g. "-activated -account_locked" vs. "activated account_locked")
       */
      if (this.sortField.replace(/-/g, '').match(sortField.replace(/-/g, ''))) {
        return this.sortField.indexOf('-') === 0
          ? this.$style.sortActiveDesc : this.$style.sortActiveAsc;
      }

      return undefined;
    },

    startKeepFresh() {
      if (this.keepFresh) {
        const { keepFreshInterval } = ItemTableConsts;
        this.cancelKeepFresh();
        this.keepFreshInterval = window
          .setInterval(() => this.handleKeepFresh(), keepFreshInterval);
      }
    },

    tableBodyRowClass(item) {
      const { $style } = this;
      const classNames = [$style.tableBodyRow];
      const ID = this.getId(item);
      if (!this.inDetailsAsidePanel && !this.inModal && !this.inSecondaryMainPanel) {
        classNames.push($style.mainPanelTableBodyRow);
      }

      if (ID === this.selectedModelId || this.selectedItems.includes(ID)) {
        classNames.push($style.tableBodyRowSelected);
      } else {
        classNames.push($style.tableBodyRowNotSelected);
      }

      if (this.legacyRowStyles) {
        classNames.push($style.legacyItemTableRow);
      }
      return classNames;
    },

    toggleHeader() {
      if (!this.showLoadingOverlay
          && !this.searchTerm
          && this.$refs.itemTableEmptyMessage) {
        const eventType = this.showEmptyMessage ? 'hide-aside-header' : 'show-aside-header';
        const event = new Event(eventType, { bubbles: true });
        this.$refs.itemTableEmptyMessage.$el.dispatchEvent(event);
      }
    },
  },
};
</script>

<style scoped module>
@import './css/ItemTable.css';
</style>
